import { useMemo } from 'react'
import { useQuery } from 'react-query'
import {
    getCashbox,
    getCashboxDrawables,
    getCashboxMembers,
    getCashboxTransactions,
    getCashboxWinners,
} from 'services/cashbox'

export const CASHBOX_KEY = (id?: string) => ['cashbox', id]

export function useCashbox(id?: string) {
    const { data, isLoading, isError, refetch } = useQuery(
        CASHBOX_KEY(id),
        getCashbox,
        { enabled: Boolean(id) }
    )
    return { cashbox: data as ICashboxDetail | undefined, isLoading, isError, refetch }
}

export function useCashboxMembers(id?: string, q?: string) {
    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-members', id, q],
        () => getCashboxMembers(id!, { q }),
        { enabled: Boolean(id) }
    )
    return { data, isLoading, isError, refetch }
}

export function useCashboxTransactions(id?: string, q?: string) {
    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-transactions', id, q],
        () => getCashboxTransactions(id!, { q }),
        { enabled: Boolean(id) }
    )
    return { data, isLoading, isError, refetch }
}

export function useCashboxWinners(id?: string, q?: string) {
    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-winners', id, q],
        () => getCashboxWinners(id!, { q }),
        { enabled: Boolean(id) }
    )
    return { data, isLoading, isError, refetch }
}

export function useCashboxDrawables(id?: string, enabled = false) {
    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-drawables', id],
        () => getCashboxDrawables(id!),
        { enabled: Boolean(id) && enabled }
    )
    return { data, isLoading, isError, refetch }
}

export interface ShareGroupView {
    group: ICashboxShareGroup
    members: Array<ICashboxMembership>
    /** sum of the members' `share_residue` */
    total: number
    /** true when the residues add up to exactly one share */
    isBalanced: boolean
}

/**
 * Fold the flat membership list into "whole-share members" + "خرده‌سهم groups".
 *
 * The backend hands back memberships and share groups as two independent
 * lists with only `membership.share_group` linking them, and it does NOT
 * guarantee the link is coherent:
 *
 *   - `share_group_function_views.update_share_group` never detaches members
 *     dropped from a group (the reset loop is commented out), so a group
 *     edited from two members down to one keeps the old member and ends up
 *     holding more than one share's worth of residue;
 *   - `kick_out_share_group` mutates member-by-member with no transaction and
 *     returns 400 halfway through on the first member with a successful
 *     transaction, leaving the rest already detached;
 *   - a group can end up with no members at all, while `Period.total_shares`
 *     still counts it.
 *
 * Rather than render those states as if they were fine, we compute the group
 * total here and mark it, so the UI can say so out loud.
 */
export function useShareGroupView(
    members: Array<ICashboxMembership> | undefined,
    shareGroups: Array<ICashboxShareGroup> | undefined,
    shareValue: number
) {
    return useMemo(() => {
        const list = members ?? []
        const groups = shareGroups ?? []
        const byGroup = new Map<number, Array<ICashboxMembership>>()
        const whole: Array<ICashboxMembership> = []

        list.forEach((m) => {
            if (m.share_group) {
                const bucket = byGroup.get(m.share_group) ?? []
                bucket.push(m)
                byGroup.set(m.share_group, bucket)
            }
            // A member can hold whole shares AND sit in a group; show them in
            // both places rather than dropping one of the two facts.
            if (m.number_of_shares > 0) whole.push(m)
        })

        const groupViews: Array<ShareGroupView> = groups.map((group) => {
            const groupMembers = byGroup.get(group.id) ?? []
            const total = groupMembers.reduce(
                (sum, m) => sum + (m.share_residue || 0),
                0
            )
            return {
                group,
                members: groupMembers,
                total,
                isBalanced: groupMembers.length > 0 && total === shareValue,
            }
        })

        return { wholeShareMembers: whole, groupViews }
    }, [members, shareGroups, shareValue])
}
